// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "features/timeline/actions"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import { ListTagsByCategoryRequest, ListTagsByCategoryResponse, } from "generated/legebill/site/tag/v4/tag_pb"
import { grpc } from "app/rpc/executor"
import { tagServiceClient } from "app/rpc/client"
import { throttleSame } from "../../../lib/rxutils"
import { isGrpcError } from "../../../lib/rpcutils"

import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type ListTagsByCategoryRpc = (payload: Actions.ListTagsByCategoryPayload) => Observable<ListTagsByCategoryResponse>

export const listTagsByCategory = (listTagsByCategoryRpc: ListTagsByCategoryRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTagsByCategory.match),
      throttleSame(2000, ({ payload: a }, { payload: b }) => a.tagCategoryId === b.tagCategoryId),
      mergeMap(({ payload }) =>
        of(payload)
          .pipe(
            mergeMap(listTagsByCategoryRpc),
            map(response => Slice.setTags(response.toObject().tagsList)),
            catchError(e => handleError(e, payload.swallowFailure)),
          )
      ),
    )

const listTagsByCategoryRpc = (payload: Actions.ListTagsByCategoryPayload): Observable<ListTagsByCategoryResponse> =>
  grpc(() => {
    const request = new ListTagsByCategoryRequest().setTagCategoryId(payload.tagCategoryId)
    return tagServiceClient.listTagsByCategory(request, null)
  })

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isGrpcError(error)) {
    const { code } = error
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(TimelineActions.onTimelineOutOfSync()),
        of(showSnackBar({
          message: t({
            id: "msg_list_tags_by_category_not_found",
            message: "Tag category not found.",
          }),
          messageType: "ERROR",
        }))
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({
        id: "msg_list_tags_by_category_failed",
        message: "Failed to load tags for category. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      }),
      messageType: "ERROR",
    }))
}

export default listTagsByCategory(listTagsByCategoryRpc)
